/* eslint-disable react/jsx-curly-newline */
import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertBanner,
  Box,
  Button,
  FooterActionBar,
  InputField,
  MenuInput,
  Typography,
} from '@saviynt/design-system';
import classNames from 'classnames';
import { useSelector } from 'react-redux';

import { type RootState } from '../../../../../services/types';
import { isEmpty, useInvertedLocalize } from '../../../../../utils';
import {
  CONFIG_ATTRIBUTES,
  PROVIDER_NAMES,
} from '../../../data-layer/constants';
import { type IDPConfig, type Provider } from '../../../data-layer/types';
import { usePostIDProvider, useUpdateIDPProvider } from '../../../hooks';
import { Header } from '../../common';

import invertedMessages from './messages';

import './IDDataweb.css';

interface Option {
  value: string;
  label: string;
}

interface IDPSetupPageProps {
  isOpen: boolean;
  onClose: () => void;
  provider: Provider | undefined;
  currentProvider: IDPConfig | undefined;
  refetchIDPProviders: () => void;
  setShowSuccessBanner: (val: boolean) => void;
}

function IDDataWebSettings({
  isOpen,
  onClose,
  provider,
  currentProvider,
  refetchIDPProviders,
  setShowSuccessBanner,
}: IDPSetupPageProps) {
  const [username, setUsername] = useState(currentProvider?.config?.USERNAME);
  const [password, setPassword] = useState(currentProvider?.config?.PASSWORD);
  const [apiKey, setApiKey] = useState(currentProvider?.config?.APIKEY);
  const [showCriticalBanner, setShowCriticalBanner] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [redirectURL, setRedirectURL] = useState(
    currentProvider?.config?.REDIRECT_URL
  );
  const [environment, setEnvironment] = useState(currentProvider?.environment);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isErrorAlertVisible, setIsErrorAlertVisible] = useState(false);
  const { isMenuPinned } = useSelector((state: RootState) => state.user);

  const l = useInvertedLocalize(invertedMessages);

  const resetBanners = () => {
    setShowCriticalBanner(false);
  };

  const initialENVOptions = [
    { label: l('Production'), value: 'Production' },
    { label: l('Preproduction'), value: 'Preproduction' },
  ];

  useEffect(() => {
    if (currentProvider) {
      setUsername(currentProvider.config?.USERNAME);
      setPassword(currentProvider.config?.PASSWORD);
      setApiKey(currentProvider.config?.APIKEY);
      setRedirectURL(currentProvider.config?.REDIRECT_URL);
      setEnvironment(currentProvider.environment);
    }
  }, [currentProvider]);

  const { mutate: saveIDPConfig } = usePostIDProvider();
  const { mutate: updateIDPConfig, isPending: isUpdateInProgress } =
    useUpdateIDPProvider();

  const handleSave = () => {
    resetBanners();

    if (provider && !isEmpty(currentProvider)) {
      const vendorConfig = {
        vendorName: provider.id,
        environment,
        status: 0,
        config: {
          [CONFIG_ATTRIBUTES.USERNAME]: username,
          [CONFIG_ATTRIBUTES.APIKEY]: apiKey,
          [CONFIG_ATTRIBUTES.REDIRECT_URL]: redirectURL,
        },
      };

      if (
        !isEmpty(currentProvider) &&
        redirectURL?.trim() !== '' &&
        !(
          redirectURL?.startsWith('http://') ||
          redirectURL?.startsWith('https://')
        )
      ) {
        setIsErrorAlertVisible(true);

        return;
      }

      setIsErrorAlertVisible(false);

      if (!isEmpty(currentProvider)) {
        vendorConfig.status = currentProvider?.status ?? 0;
      }

      if (
        !isEmpty(currentProvider) &&
        password !== currentProvider?.config?.PASSWORD
      ) {
        vendorConfig.config.PASSWORD = password;
      }

      updateIDPConfig(vendorConfig, {
        onSuccess(response) {
          if (response.errorMessage) {
            setErrorMessage(response.errorMessage);
            setShowCriticalBanner(true);
          } else {
            onClose();
            refetchIDPProviders();
            setShowSuccessBanner(true);
          }
        },
      });
    } else if (isEmpty(currentProvider)) {
      const vendorConfig = {
        vendorName: PROVIDER_NAMES.IDDATAWEB,
        environment,
        status: 0,
        config: {
          USERNAME: username,
          APIKEY: apiKey,
          PASSWORD: password,
          REDIRECT_URL: redirectURL,
        },
      };

      saveIDPConfig(vendorConfig, {
        onSuccess(response) {
          if (response.errorMessage) {
            setErrorMessage(response.errorMessage);
            setShowCriticalBanner(true);
          } else {
            onClose();
            refetchIDPProviders();
          }
        },
      });
    }
  };

  const userNameField = useMemo(
    () => (
      <InputField
        name='username'
        label={
          <Typography kind='label' htmlFor='Username'>
            {l('Username')}
          </Typography>
        }
        placeholder={l('Enter Username')}
        value={username}
        setValue={setUsername}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setUsername(e.target.value)
        }
        className='Ext-IDDataWebSettings-content-inputField'
        isRequired
        maxLength={100}
        minRequiredChars={3}
      />
    ),
    [username, setUsername, l]
  );

  const apiKeyField = useMemo(
    () => (
      <InputField
        name='apiKey'
        label={
          <Typography kind='label' htmlFor='API Key'>
            {l('API Key')}
          </Typography>
        }
        placeholder={l('Enter API Key')}
        value={apiKey}
        setValue={setApiKey}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setApiKey(e.target.value)
        }
        className='Ext-IDDataWebSettings-content-inputField'
        isRequired
        maxLength={100}
        minRequiredChars={3}
      />
    ),
    [apiKey, setApiKey, l]
  );

  const passwordField = useMemo(
    () => (
      <InputField
        name='password'
        type={isPasswordVisible ? 'text' : 'password'}
        suffixButtonType='password'
        suffixIsToggle={isPasswordVisible}
        suffixOnClick={() => setIsPasswordVisible((prev) => !prev)}
        label={
          <Typography kind='label' htmlFor='Password'>
            {l('Password')}
          </Typography>
        }
        placeholder={l('Enter Password')}
        value={password}
        setValue={setPassword}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setPassword(e.target.value)
        }
        className='Ext-IDDataWebSettings-content-inputField'
        isRequired
        maxLength={100}
        minRequiredChars={3}
        shouldDisallowBrackets={false}
      />
    ),
    [password, setPassword, isPasswordVisible, l]
  );

  const redirectURLField = useMemo(
    () => (
      <InputField
        name='redirectURL'
        label={
          <Typography kind='label' htmlFor='Redirect URL'>
            {l('Redirect URL')}
          </Typography>
        }
        placeholder={l('Enter Redirect URL (e.g., https://www.example.com)')}
        value={redirectURL}
        setValue={setRedirectURL}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setRedirectURL(e.target.value)
        }
        className='Ext-IDDataWebSettings-content-inputField'
        maxLength={255}
        minRequiredChars={3}
        isCritical={isErrorAlertVisible}
        setIsCritical={setIsErrorAlertVisible}
      />
    ),
    [
      redirectURL,
      setRedirectURL,
      l,
      isErrorAlertVisible,
      setIsErrorAlertVisible,
    ]
  );

  const isSaveDisabled =
    isEmpty(username) ||
    isEmpty(password) ||
    isEmpty(apiKey) ||
    isEmpty(environment) ||
    isUpdateInProgress;

  return (
    <div
      className='Ext-IDDataWebSettings'
      role='dialog'
      aria-labelledby='setUpPageTitle'
      aria-hidden={!isOpen}>
      <div className='Ext-IDDataWebSettings-contentWrapper'>
        {provider ? (
          <Header
            currentProvider={currentProvider}
            provider={provider}
            resetBanners={resetBanners}
          />
        ) : (
          ''
        )}
        <AlertBanner
          colorTheme='Warning'
          kind='low'
          title={l('Invalid input')}
          description={l('Please enter valid redirect URL.')}
          isVisible={!isSaveDisabled && isErrorAlertVisible}
          onCancel={() => setIsErrorAlertVisible(false)}
          shouldAutoDismiss
          isFloating
          position={{
            type: 'absolute',
            top: '8rem',
            left: '7rem',
          }}
        />
        <AlertBanner
          colorTheme='Critical'
          title={errorMessage}
          description=''
          isVisible={showCriticalBanner}
          onCancel={() => setShowCriticalBanner(false)}
          isFloating
          position={{
            type: 'absolute',
            top: '8rem',
            left: '7rem',
          }}
          shouldAutoDismiss
          isColonVisible={false}
        />
        <div className='Ext-IDDataWebSettings-content'>
          {userNameField}
          {passwordField}
          {apiKeyField}
          {redirectURLField}
          <Box>
            <Typography kind='label' htmlFor='Environment'>
              {l('Environment')}
            </Typography>
            <MenuInput
              options={initialENVOptions.map((option) => ({
                label: option.label,
                value: option.value,
                isChecked: option.value === environment,
              }))}
              onChange={(selectedOption: Option) =>
                setEnvironment(selectedOption.value)
              }
              placeholder={l('Select Environment')}
              className='Ext-IDDataWebSettings-content-menuInput'
              isSearchDisabled
            />
          </Box>
        </div>
      </div>
      <div
        className={classNames(
          'Ext-IDDataWebSettings-footer',
          isMenuPinned && 'Ext-IDDataWebSettings-footer--isMenuPinned'
        )}>
        <FooterActionBar>
          <Button
            kind='outlined'
            onClick={() => {
              resetBanners();
              onClose();
            }}>
            {l('Back')}
          </Button>
          <Button
            kind='filled'
            onClick={handleSave}
            isLoading={isUpdateInProgress}
            loaderText={l('Saving')}
            isDisabled={isSaveDisabled}>
            {l('Save')}
          </Button>
        </FooterActionBar>
      </div>
    </div>
  );
}

export default IDDataWebSettings;
